import type { FC } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { AddPersonIcon, ContactsIcon } from '@setup/svg-icons/svg-icons';
import { ModalNamesEnum } from '@shared/enums/modalNames.enum';
import { transformToAssignUserSelectValueWithRole } from '@shared/functions/transformToAssignUserSelectValueWithRole';
import { useAppDispatch } from '@shared/hooks/redux';
import { NoOptionsItem } from '@shared/ui-kit/selects/MuiSelect/NoOptionsMessage/components/NoOptionsItem/NoOptionsItem';
import { createContact, invite } from '@store/network/networkThunk';

import modalObserver from '../../../../utils/observers/modalObserver';

import { NoOptionsMessageContainer, NoResultFoundContainer } from './NoOptionsMessage.style';

import type { UserShortInfoFormDataType } from '@components/forms/UserShortInfoForm/UserShortInfoForm';
import type { ItemUserModel } from '@shared/models/itemUser.model';
import type { OptionType } from '@shared/ui-kit';

interface NoOptionsMessagePropsType {
  isCreateContact?: boolean;
  isCreateOnlyCompanyContact?: boolean;
  onChangeAssignPeopleListValue: (option: ItemUserModel[] | OptionType[], actionMeta: any) => void;
  assignPeopleListValue: OptionType[];
}

export const NoOptionsMessage: FC<NoOptionsMessagePropsType> = ({
  isCreateContact = true,
  isCreateOnlyCompanyContact = false,
  onChangeAssignPeopleListValue,
  assignPeopleListValue,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleChangeAssignPeopleListValue = (assignUser: ItemUserModel) => {
    onChangeAssignPeopleListValue(
      [...assignPeopleListValue, ...transformToAssignUserSelectValueWithRole([assignUser])] as
        | ItemUserModel[]
        | OptionType[],
      'select-option',
    );
  };

  const openCreateContactModal = (isCompanyContact: boolean) => {
    modalObserver.addModal(
      isCompanyContact ? ModalNamesEnum.createCompanyContactModal : ModalNamesEnum.createContactShortModal,
      {
        props: {
          handleAddToAssignList: (createUser: ItemUserModel) => {
            handleChangeAssignPeopleListValue(createUser);
          },
        },
      },
    );
  };

  const handleCreateContact = (data: UserShortInfoFormDataType) => {
    dispatch(createContact({ data })).then((result) => {
      if (createContact.fulfilled.match(result)) {
        const contactFullName = result.payload.contacts.is_company
          ? result.payload.contacts.company
          : result.payload.full_name;

        NotificationService.success(t('network.toasts.contactCreated', { contactFullName }));

        if (isCreateContact || isCreateOnlyCompanyContact) {
          handleChangeAssignPeopleListValue(result.payload);
        }
      }
    });
  };

  const dataSubmitCustomFunction = (data: UserShortInfoFormDataType) => {
    const contactFullName = data.contacts?.is_company
      ? data.contacts?.company
      : `${data.first_name} ${data.last_name}`;

    dispatch(invite({ data })).then((result) => {
      if (invite.fulfilled.match(result)) {
        NotificationService.success(t('network.toasts.sentInviteContact', { contactFullName }));
        modalObserver.removeModal(ModalNamesEnum.invitationModal);
        modalObserver.addModal(ModalNamesEnum.alertModal, {
          props: {
            modalContent: {
              variantIcon: 'done',
              header: t('network.inviteContactDoneModal.header'),
              title: t('network.inviteContactDoneModal.title'),
              customSubtitle: (
                <Typography
                  sx={{ textAlign: 'center', color: theme.palette.case.neutral.n800 }}
                  variant="t14r"
                >
                  <Trans
                    i18nKey="network.inviteContactDoneModal.subtitle"
                    values={{
                      hubmee: t('general.hubmee'),
                      contactName: contactFullName,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: '500', color: theme.palette.case.aquamarine.a700 }}
                    >
                      {t('general.hubmee')}
                    </Box>
                  </Trans>
                </Typography>
              ),
              isShowFooter: true,
            },
            rightBtnProps: {
              isShow: true,
              label: t('general.buttons.create'),
              onClick: () => {
                modalObserver.removeModal(ModalNamesEnum.alertModal);
                handleCreateContact(data);
              },
            },
            middleBtnProps: {
              isShow: true,
              label: t('general.buttons.cancel'),
              onClick: () => modalObserver.removeModal(ModalNamesEnum.alertModal),
              color: 'secondary',
              variant: 'outlined',
            },
          },
        });
      }
    });
  };

  const inviteToHubmee = () => {
    modalObserver.addModal(ModalNamesEnum.invitationModal, {
      props: {
        dataSubmitCustomFunction,
      },
    });
  };

  return (
    <NoOptionsMessageContainer>
      {isCreateOnlyCompanyContact ? (
        <NoOptionsItem
          onClick={() => openCreateContactModal(true)}
          icon={<ContactsIcon />}
          buttonText={t('general.buttons.createCompanyContact')}
        />
      ) : (
        <>
          {isCreateContact && (
            <NoOptionsItem
              onClick={() => openCreateContactModal(false)}
              icon={<ContactsIcon />}
              buttonText={t('general.buttons.createContact')}
            />
          )}

          <NoOptionsItem
            onClick={inviteToHubmee}
            icon={<AddPersonIcon />}
            buttonText={t('general.buttons.inviteToHubmee')}
          />
        </>
      )}

      <NoResultFoundContainer>
        <Typography sx={{ color: theme.palette.case.neutral.n500 }} variant="t14r">
          {t('network.noResultFound')}
        </Typography>
      </NoResultFoundContainer>
    </NoOptionsMessageContainer>
  );
};
